import { createMediaSyncDiagnosticRecorder } from '@/services/diagnostics/mediaDiagnosticRuntime';
import { publishChecklistMediaTerminal } from '@/services/checklist/ChecklistMediaTerminalEvents';
import { sha512Hex } from './PendingOperationTerminalization';
import { databaseService } from '@/services/databaseService';
import {
  assertPendingSyncSessionCurrent, createPendingSyncSupabaseClient,
  isPendingSyncSessionSnapshotStateCurrent, type PendingSyncSessionSnapshot,
} from './PendingSyncSession';

/** Confirma o objeto e a referência remota antes de retirar a fila ativa; os bytes locais permanecem retidos. */
export async function finalizeAcknowledgedChecklistMedia(input: {
  pending: Record<string, any>; value: Record<string, any>; publicUrl: string;
  snapshot: PendingSyncSessionSnapshot; recoveryReceiptId?: string;
  storageLocation?: { bucket: string; path: string };
}): Promise<boolean> {
  const { pending, value, publicUrl, snapshot } = input;
  const record = createMediaSyncDiagnosticRecorder({ companyId: snapshot.empresaId, userId: snapshot.userId,
    checklistId: pending.item_id, fieldId: pending.campo_id, attemptId: pending.id,
    valueId: value.id, mediaRef: publicUrl,
  });
  const deferred = (reason: Parameters<typeof record>[1], error?: unknown): false => {
    record('reference_ack_deferred', reason, error);
    return false;
  };
  try {
    if (value.empresa_id !== snapshot.empresaId || value.checklist_id !== pending.item_id ||
        (!input.recoveryReceiptId && value.snapshot_field_id !== pending.campo_id && value.campo_id !== pending.campo_id) ||
        (value.valor !== publicUrl && value.valor !== pending.local_uri)) return deferred('identity_mismatch');
    let recoveryReceipt: Record<string, any> | undefined;
    let remoteCampoId = value.campo_id ?? null;
    let remoteSnapshotFieldId = value.snapshot_field_id ?? null;
    let location = { bucket: pending.bucket, path: pending.path };
    if (input.recoveryReceiptId) {
      const row = await databaseService.readItem<any>('sync_conflicts', input.recoveryReceiptId);
      if (!row || row.empresa_id !== snapshot.empresaId || row.user_id !== snapshot.userId ||
          row.reason !== 'legacy_media_explicit_recovery' || row.payload_sha512 !== sha512Hex(row.payload)) return deferred('recovery_receipt_invalid');
      const receipt = JSON.parse(row.payload);
      recoveryReceipt = row;
      if (pending.user_id || receipt.schema !== 'checklist-legacy-media-recovery/v1' ||
          receipt.originalAuthor !== 'unknown' || receipt.recoveryActor !== snapshot.userId ||
          receipt.companyId !== snapshot.empresaId || JSON.stringify(receipt.pending) !== JSON.stringify(pending) ||
          receipt.value.id !== value.id || receipt.value.empresa_id !== value.empresa_id ||
          receipt.value.checklist_id !== value.checklist_id ||
          (value.valor !== receipt.pending.local_uri && value.valor !== publicUrl) ||
          receipt.bucket !== input.storageLocation?.bucket || receipt.path !== input.storageLocation?.path) return deferred('recovery_receipt_invalid');
      location = { bucket: receipt.bucket, path: receipt.path };
      remoteSnapshotFieldId = receipt.snapshotFieldId;
      remoteCampoId = receipt.snapshotFieldId && receipt.referenceMode !== 'physical' ? null : receipt.value.campo_id;
    } else {
      // A autoria é a origem preservada da fila. Qualquer usuário da mesma
      // empresa pode transmitir, desde que o vínculo exato checklist/valor/URL
      // seja confirmado abaixo; metadados contraditórios continuam bloqueando.
      const pendingCompany = typeof pending.empresa_id === 'string' ? pending.empresa_id.trim() : '';
      if (pending.item_type !== 'CHECKLIST' || input.storageLocation ||
          (pendingCompany && pendingCompany !== snapshot.empresaId) ||
          (pending.empresa_id !== null && pending.empresa_id !== undefined && typeof pending.empresa_id !== 'string')) return deferred('identity_mismatch');
    }
    const client = createPendingSyncSupabaseClient(snapshot);
    if (client.storage.from(location.bucket).getPublicUrl(location.path).data.publicUrl !== publicUrl) return deferred('path_mismatch');
    await assertPendingSyncSessionCurrent(snapshot);
    const { data, error } = await client.from('guincho_valores_checklist')
      .select('id,empresa_id,checklist_id,campo_id,snapshot_field_id,valor,observacao,created_at,updated_at')
      .eq('id', value.id).eq('empresa_id', snapshot.empresaId).eq('checklist_id', pending.item_id).maybeSingle();
    await assertPendingSyncSessionCurrent(snapshot);
    if (error) return deferred('remote_read_failed', error);
    if (!data) return deferred('remote_reference_missing');
    if (typeof data.updated_at !== 'string' || !Number.isFinite(Date.parse(data.updated_at)) || data.id !== value.id || data.empresa_id !== snapshot.empresaId ||
        data.checklist_id !== pending.item_id || data.valor !== publicUrl) return deferred('remote_reference_mismatch');
    const enrichedSnapshot = !input.recoveryReceiptId && remoteSnapshotFieldId === null &&
      remoteCampoId !== null && Boolean(data.snapshot_field_id);
    if ((data.snapshot_field_id ?? null) !== remoteSnapshotFieldId && !enrichedSnapshot) return deferred('snapshot_mismatch');
    if ((data.campo_id ?? null) !== remoteCampoId || enrichedSnapshot) {
      const candidateSnapshotFieldId = enrichedSnapshot ? data.snapshot_field_id : remoteSnapshotFieldId;
      if (input.recoveryReceiptId || !candidateSnapshotFieldId) return deferred('snapshot_mismatch');
      const field = await client.from('guincho_checklist_snapshot_fields').select('id,empresa_id,snapshot_id,original_campo_id')
        .eq('id', candidateSnapshotFieldId).eq('empresa_id', snapshot.empresaId).maybeSingle();
      const parent = await client.from('guincho_checklists').select('id,empresa_id,snapshot_id,retired_at')
        .eq('id', pending.item_id).eq('empresa_id', snapshot.empresaId).maybeSingle();
      await assertPendingSyncSessionCurrent(snapshot);
      if (field.error || parent.error || !field.data || !parent.data || field.data.id !== candidateSnapshotFieldId ||
          field.data.empresa_id !== snapshot.empresaId || !field.data.original_campo_id ||
          parent.data?.id !== pending.item_id || parent.data.empresa_id !== snapshot.empresaId || parent.data.retired_at ||
          parent.data.snapshot_id !== field.data.snapshot_id ||
          (remoteCampoId !== null && remoteCampoId !== field.data.original_campo_id) ||
          (data.campo_id !== null && data.campo_id !== field.data.original_campo_id)) return deferred('snapshot_mismatch');
      remoteCampoId = data.campo_id;
      remoteSnapshotFieldId = candidateSnapshotFieldId;
    }
    const { data: stored, error: storageError } = await client.storage.from(location.bucket).info(location.path);
    await assertPendingSyncSessionCurrent(snapshot);
    if (storageError || !stored) return deferred('storage_unconfirmed', storageError);
    record('reference_remote_confirmed');
    const finalization = { pending, value, publicUrl, remoteUpdatedAt: data.updated_at, remoteObservation: data.observacao ?? null, remoteSnapshotFieldId, remoteCampoId, recoveryReceipt,
      canAcknowledge: () => isPendingSyncSessionSnapshotStateCurrent(snapshot) };
    let completed = await databaseService.finalizeChecklistMedia(finalization);
    if (!completed) {
      await assertPendingSyncSessionCurrent(snapshot);
      const currentValue = await databaseService.readItem<Record<string, any>>('guincho_valores_checklist', value.id);
      await assertPendingSyncSessionCurrent(snapshot);
      const changedKeys = currentValue && [...new Set([...Object.keys(value), ...Object.keys(currentValue)])].filter(key =>
        (currentValue[key] ?? null) !== (value[key] ?? null));
      // O Realtime pode gravar a mesma linha remota entre a leitura e o CAS.
      // Só repetimos uma vez quando cada diferença local já está no servidor;
      // o CAS da fila original continua protegendo uma nova mídia pendente.
      if (currentValue && changedKeys?.length && changedKeys.every(key =>
        Object.prototype.hasOwnProperty.call(data, key) && (currentValue[key] ?? null) === ((data as Record<string, any>)[key] ?? null))) {
        completed = await databaseService.finalizeChecklistMedia({ ...finalization, value: currentValue });
      } else if (currentValue && changedKeys?.length &&
          changedKeys.every(key => key === 'observacao' || key === 'updated_at') &&
          changedKeys.includes('observacao') &&
          currentValue.id === data.id && currentValue.empresa_id === snapshot.empresaId &&
          currentValue.checklist_id === pending.item_id && currentValue.valor === publicUrl &&
          (currentValue.campo_id ?? null) === (value.campo_id ?? null) &&
          (currentValue.snapshot_field_id ?? null) === (value.snapshot_field_id ?? null) &&
          (data.campo_id ?? null) === (remoteCampoId ?? null) &&
          (data.snapshot_field_id ?? null) === (remoteSnapshotFieldId ?? null) &&
          (currentValue.observacao ?? null) !== (data.observacao ?? null)) {
        completed = await databaseService.finalizeChecklistMedia({
          ...finalization,
          value: currentValue,
          expectedObservation: currentValue.observacao ?? null,
        });
      }
    }
    if (!completed) return deferred('local_cas_rejected');
    await assertPendingSyncSessionCurrent(snapshot);
    const confirmedValue = await databaseService.readItem<Record<string, any>>('guincho_valores_checklist', value.id);
    if (confirmedValue?.valor === publicUrl) publishChecklistMediaTerminal({ companyId: snapshot.empresaId,
      checklistId: pending.item_id, fieldId: pending.campo_id, expectedValue: value.valor,
      localUri: pending.local_uri, uri: publicUrl, expectedObservation: value.observacao ?? null,
      observation: confirmedValue.observacao ?? null });
    await assertPendingSyncSessionCurrent(snapshot);
    // O ACK confirma o vínculo. Os bytes ficam sob custódia da retenção durável;
    // uma leitura seguida de DELETE aqui permitiria apagar uma referência concorrente.
    record('reference_acknowledged');
    return true;
  } catch (error) {
    record('reference_ack_failed', error instanceof Error && error.name === 'PendingSyncSessionChangedError'
      ? 'session_changed' : 'ack_exception', error);
    throw error;
  }
}
